import { Component, type ReactNode } from 'react';
import { reportError } from '../lib/sentry';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Last-resort boundary around the whole app (providers, the router itself). React Router's
 * `errorElement` only catches errors inside the routed tree — this catches everything else,
 * so a bad render never leaves the user staring at a blank white page.
 * Class component: error boundaries have no hook-based equivalent in React yet.
 */
export class RootErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    reportError(error);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    // Plain markup, no i18n/UI-kit dependency — those providers may be exactly what threw.
    const isAr = document.documentElement.lang !== 'en';
    return (
      <div dir={isAr ? 'rtl' : 'ltr'} style={{ display: 'flex', minHeight: '100dvh', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <p style={{ fontSize: 18, fontWeight: 700 }}>{isAr ? 'حدث خطأ غير متوقع' : 'Something went wrong'}</p>
        <p style={{ fontSize: 14, color: '#6b7280', maxWidth: 420 }}>{isAr ? 'حاول إعادة تحميل الصفحة. إن استمرت المشكلة تواصل مع الدعم.' : 'Try reloading the page. Contact support if this keeps happening.'}</p>
        <button
          onClick={() => window.location.reload()}
          style={{ borderRadius: 10, background: '#0F3D26', color: '#fff', padding: '10px 20px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
        >
          {isAr ? 'إعادة التحميل' : 'Reload'}
        </button>
      </div>
    );
  }
}
